import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import type { SubdivisionDto, SubdivisionStockRow } from '@eombor/contracts';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Skeleton,
  Table,
  cn,
  useToast,
} from '@eombor/ui';
import type { Column } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { orgApi } from '../../org/api/org.api.js';
import { subdivisionApi } from '../api/subdivision.api.js';
import {
  SubdivisionDialog,
  type SubdivisionFormValues,
  type SubdivisionOwnerOption,
} from './SubdivisionDialog.js';

/** Sana: «12.08.2026». Vaqt ko'rsatilmaydi — qoldiq kun aniqligida o'qiladi. */
const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/**
 * BO'LINMALAR — chapda ro'yxat, o'ngda tanlanganining QOLDIG'I.
 *
 * Ikki narsa bitta ekranda, chunki ular bitta savolga javob beradi: «R va AX
 * bo'limida nima bor?» Ro'yxat va qoldiq ikki bo'limga ajratilsa, javob
 * uchun ikki ekran orasida yurish kerak bo'lardi.
 *
 * Bo'linma nomi ro'yxatda hech qachon YOLG'IZ turmaydi: «R va AX» ham Qarshi
 * MTU apparatida, ham uning ichidagi SHCH-8 da bor va ular boshqa-boshqa
 * bo'limlar. Egasi shu sababli har satrda ko'rinadi.
 */

const stockColumns: Column<SubdivisionStockRow>[] = [
  {
    key: 'name',
    header: t('subdivision.colNomenclature'),
    render: (row) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-text">{row.nomenclatureName}</span>
        <span className="tabular text-xs text-text-faint">{row.batchNo}</span>
      </div>
    ),
  },
  {
    key: 'qty',
    header: t('subdivision.colQty'),
    align: 'right',
    mono: true,
    render: (row) => `${row.qty} ${row.unit}`,
  },
  {
    key: 'received',
    header: t('subdivision.colReceived'),
    render: (row) => (
      <div className="flex min-w-0 flex-col">
        <span className="tabular text-text">{formatDate(row.receivedAt)}</span>
        {/* Qaysi talabnoma bilan kelgani — «bu qayerdan?» savoliga javob. */}
        <span className="truncate text-xs text-text-faint">
          {row.requisitionDocNo ?? '—'}
          {row.receivedByName === null ? '' : ` · ${row.receivedByName}`}
        </span>
      </div>
    ),
  },
];

export const SubdivisionsPage = () => {
  const { can, canAnywhere, isSuperadmin } = usePermission();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [mtuId, setMtuId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SubdivisionDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SubdivisionDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const canManage = canAnywhere('subdivision.manage');

  const mtus = useQuery({
    queryKey: ['org', 'units', 'mtu'],
    queryFn: () => orgApi.listUnits({ type: 'MTU' }),
    staleTime: 5 * 60_000,
  });

  const subdivisions = useQuery({
    queryKey: ['subdivisions', mtuId, search],
    queryFn: () =>
      subdivisionApi.list({
        mtuId: mtuId === '' ? undefined : mtuId,
        search: search === '' ? undefined : search,
      }),
  });

  const items = subdivisions.data?.items ?? [];
  const selected = items.find((row) => row.id === selectedId) ?? null;

  /*
   * Egasi bo'la oladigan korxonalar.
   *
   * Ro'yxat sahifadagi MTU filtriga BOG'LANMAYDI. Bir marta bog'langan edi
   * va natijada oyna jimgina bo'sh ochilardi: filtr qo'yilmagan bo'lsa,
   * so'rov umuman yuborilmasdi va foydalanuvchi «korxona chiqmadi» degan
   * boshi berk ko'chaga kirardi.
   *
   * Filtr qo'yilgan bo'lsa u ro'yxatni QISQARTIRADI — bu yordam, shart emas.
   * Doirani baribir server qo'yadi: xodim ko'ra olmaydigan korxona ro'yxatga
   * umuman tushmaydi.
   *
   * MTU ning O'ZI ham ro'yxatda qoladi: uning apparatida ham bo'lim bor
   * («Qarshi MTU — R va AX»).
   */
  const owners = useQuery({
    queryKey: ['org', 'units', 'owners', mtuId],
    queryFn: () => orgApi.listAllUnits({ mtuId: mtuId === '' ? undefined : mtuId }),
    enabled: dialogOpen && editing === null,
    staleTime: 5 * 60_000,
  });

  const ownerOptions: SubdivisionOwnerOption[] = useMemo(
    () =>
      (owners.data ?? [])
        .filter((unit) => unit.isActive)
        .map((unit) => ({
          id: unit.id,
          name: unit.name,
          /* KOD, `shortName` emas: qisqa nom noyob emas va bir xil nomli
             ikki korxonada u ko'pincha bir xil bo'ladi. */
          code: unit.code,
          mtuId: unit.mtuId,
        })),
    [owners.data],
  );

  const stock = useQuery({
    queryKey: ['subdivisions', 'stock', selected?.id ?? ''],
    queryFn: () => subdivisionApi.stock(selected?.id ?? ''),
    enabled:
      selected !== null &&
      can('subdivision.stock.read', {
        mtuId: selected.mtuId ?? undefined,
        orgUnitId: selected.orgUnitId,
      }),
  });

  const save = useMutation({
    mutationFn: (values: SubdivisionFormValues) => {
      if (editing !== null) {
        return subdivisionApi.update(editing.id, {
          name: values.name,
          isActive: values.isActive,
        });
      }
      /* `mtuId` — guard uchun NIYAT (backend uni egasidan qayta hisoblaydi).
         Tanlangan egasining o'z MTU si yuboriladi, sahifa filtri emas: ular
         mos kelmasa server 404 beradi va sabab ko'rinmasdi. */
      const owner = ownerOptions.find((row) => row.id === values.orgUnitId);
      return subdivisionApi.create({
        code: values.code,
        name: values.name,
        orgUnitId: values.orgUnitId,
        mtuId: owner?.mtuId ?? null,
      });
    },
    onSuccess: (row) => {
      setDialogOpen(false);
      setDialogError(null);
      setEditing(null);
      setSelectedId(row.id);
      toast.show(t('subdivision.saved'), 'success');
      void queryClient.invalidateQueries({ queryKey: ['subdivisions'] });
    },
    onError: (err) => {
      setDialogError(err instanceof ApiClientError ? err.message : t('subdivision.saveFailed'));
    },
  });

  /*
   * O'CHIRISH — FAQAT SUPERADMINGA.
   *
   * Ruxsat bilan emas, hisobning turi bilan chegaralangan: `subdivision.manage`
   * MTU administratorida ham bor, o'chirish esa qaytarilmaydi va bo'linma
   * butun bir bo'limning ombori bo'lib turadi. Haqiqiy to'siq baribir
   * serverda — bu yerda tugma shunchaki ko'rsatilmaydi.
   */
  const remove = useMutation({
    mutationFn: (id: string) => subdivisionApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      setDeleteError(null);
      setSelectedId(null);
      toast.show(t('subdivision.deleted'), 'success');
      void queryClient.invalidateQueries({ queryKey: ['subdivisions'] });
    },
    onError: (err) => {
      /* Xato oynada QOLADI: «ishlatilgan» degan javob eng muhim ma'lumot va
         u toast bilan birga o'tib ketmasligi kerak. */
      setDeleteError(err instanceof ApiClientError ? err.message : t('subdivision.deleteFailed'));
    },
  });

  const openCreate = (): void => {
    setEditing(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openDelete = (row: SubdivisionDto): void => {
    setDeleteError(null);
    setDeleting(row);
  };

  const openEdit = (row: SubdivisionDto): void => {
    setEditing(row);
    setDialogError(null);
    setDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('subdivision.title')}
        description={t('subdivision.subtitle')}
        filters={
          <>
            <Select
              hiddenLabel
              label={t('subdivision.filterMtu')}
              placeholder={t('subdivision.allMtus')}
              options={(mtus.data?.items ?? []).map((unit) => ({
                value: unit.id,
                label: unit.name,
              }))}
              value={mtuId}
              onChange={(event) => setMtuId(event.target.value)}
            />
            <Input
              variant="filter"
              aria-label={t('subdivision.searchLabel')}
              placeholder={t('subdivision.searchPlaceholder')}
              value={search}
              prefix={<Search size={16} aria-hidden />}
              onChange={(event) => setSearch(event.target.value)}
            />
          </>
        }
        actions={
          canManage ? (
            <Button onClick={openCreate}>
              <Plus size={16} aria-hidden />
              {t('subdivision.create')}
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-2 p-3">
          {subdivisions.isPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Boxes size={28} aria-hidden />}
              title={t('subdivision.emptyTitle')}
              description={t('subdivision.emptyText')}
            />
          ) : (
            <ul className="flex flex-col gap-1">
              {items.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(row.id)}
                    aria-current={row.id === selectedId ? 'true' : undefined}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 rounded-[var(--radius-sm)]',
                      'px-3 py-2 text-left transition-colors duration-[var(--motion-fast)]',
                      'ease-[var(--ease-standard)] hover:bg-surface-2',
                      row.id === selectedId ? 'bg-surface-2' : '',
                    )}
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-[15px] text-text">
                        {row.name}
                      </span>
                      {row.isActive ? null : (
                        <Badge tone="neutral">{t('subdivision.statusInactive')}</Badge>
                      )}
                    </span>
                    {/* Egasi HAR DOIM ko'rinadi va BELGISI bilan: tuzilmada
                        bir xil nomli ikki yozuv bor (MTU ning o'zi va uning
                        apparati) va faqat nom ko'rsatilsa, bo'lim qaysi
                        biriga biriktirilgani bilinmaydi. */}
                    <span className="truncate text-xs text-text-muted">
                      {row.orgUnitName} · {row.orgUnitCode}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="flex min-h-[20rem] flex-col gap-4 p-4">
          {selected === null ? (
            <EmptyState
              icon={<Boxes size={28} aria-hidden />}
              title={t('subdivision.pickTitle')}
              description={t('subdivision.pickText')}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <h2 className="text-lg font-semibold text-text">{selected.name}</h2>
                  <p className="text-sm text-text-muted">
                    {selected.orgUnitName} · <span className="tabular">{selected.code}</span>
                  </p>
                </div>
                {can('subdivision.manage', {
                  mtuId: selected.mtuId ?? undefined,
                  orgUnitId: selected.orgUnitId,
                }) ? (
                  <span className="flex gap-2">
                    <Button variant="ghost" onClick={() => openEdit(selected)}>
                      <Pencil size={16} aria-hidden />
                      {t('subdivision.edit')}
                    </Button>
                    {isSuperadmin ? (
                      <Button variant="ghost" onClick={() => openDelete(selected)}>
                        <Trash2 size={16} aria-hidden />
                        {t('subdivision.delete')}
                      </Button>
                    ) : null}
                  </span>
                ) : null}
              </div>

              {stock.isPending ? (
                <Skeleton className="h-40" />
              ) : (
                <Table
                  caption={t('subdivision.stockCaption')}
                  columns={stockColumns}
                  rows={stock.data?.items ?? []}
                  rowKey={(row) => row.batchId}
                  density="compact"
                  empty={
                    <EmptyState
                      icon={<Boxes size={28} aria-hidden />}
                      title={t('subdivision.stockEmptyTitle')}
                      description={t('subdivision.stockEmptyText')}
                    />
                  }
                />
              )}
            </>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => deleting !== null && remove.mutate(deleting.id)}
        title={t('subdivision.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('subdivision.deleteText')}
        confirmLabel={t('subdivision.deleteConfirm')}
        cancelLabel={t('subdivision.deleteCancel')}
        tone="danger"
        loading={remove.isPending}
        {...(deleteError === null ? {} : { error: deleteError })}
      />

      <SubdivisionDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogError(null);
        }}
        subdivision={editing}
        owners={ownerOptions}
        ownersPending={dialogOpen && editing === null && owners.isPending}
        onSubmit={(values) => save.mutate(values)}
        pending={save.isPending}
        error={dialogError}
      />
    </div>
  );
};
